/**
 * Returns the full CI/CD setup status for a space, used by the "Readiness"
 * column in Scene0 so the card can say more than just "ready for transport"
 * and show whether the repository, workflow and stages are actually usable.
 *
 * Backend: GET /api/cicd/pipeline/overview?spaceId=... returns
 *   { configured, health: { tokenSet, repoPrivate, workflowActive, stagesReady }, ... }
 * The endpoint requires admin; for non-admins it answers 403, which this hook
 * treats as "unknown" rather than blocking the card.
 *
 * One fetch per space, no polling loop: the space rarely changes and the
 * response involves three to four GitHub calls on the backend.
 */

import { useEffect, useState } from "react";
import { api } from "../../../utils/helpers";

const LEER = {
  loading: false,
  configured: false,
  tokenSet: false,
  repoPrivate: null,
  workflowActive: null,
  stagesReady: false,
  ready: false,
  forbidden: false,
  error: null,
};

export function usePipelineHealth(spaceId) {
  const [state, setState] = useState({ ...LEER, loading: !!spaceId });

  useEffect(() => {
    if (!spaceId) { setState(LEER); return; }
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    api(`/api/cicd/pipeline/overview?spaceId=${encodeURIComponent(spaceId)}`)
      .then((r) => {
        if (!alive) return;
        const configured = r?.configured !== false;
        const h = r?.health || {};
        const tokenSet = !!h.tokenSet;
        const workflowActive = h.workflowActive === true;
        const stagesReady = h.stagesReady === true;
        // "Ready for transport" requires everything to be in place. configured
        // and tokenSet alone are not enough: without synced stages the
        // dispatch targets a GitHub environment that does not exist.
        const ready = configured && tokenSet && workflowActive && stagesReady;
        setState({
          loading: false,
          configured,
          tokenSet,
          repoPrivate: h.repoPrivate,
          workflowActive,
          stagesReady,
          ready,
          forbidden: false,
          error: null,
        });
      })
      .catch((e) => {
        if (!alive) return;
        // 403 means not an admin. Not an error state, falls back to "unknown".
        const forbidden = e?.status === 403;
        setState({ ...LEER, loading: false, forbidden, error: forbidden ? null : (e?.message || null) });
      });
    return () => { alive = false; };
  }, [spaceId]);

  return state;
}
